import { Dispatch, FC, SetStateAction, useCallback, useMemo } from 'react';
import { MUIDataTableState } from 'mui-datatables';
import { useTranslation } from 'react-i18next';

import { Table, customHeadRender } from 'components/table';
import { CompanyTransaction } from 'store/applications/types';
import { ROWS_PER_TABLE_PAGE } from 'core/constants';
import { CompanyTransactionsColumnNames } from 'core/types';
import { parseMoney } from 'utils';
import { useApplications } from 'store/applications/hooks';
import { mapAppRegionToSupportedCurrency } from 'core/utils';

interface TransactionsTableProps {
  transactions: CompanyTransaction[];
  total: number;
  activePage: number;
  setActivePage: Dispatch<SetStateAction<number>>;
}

const TransactionsTable: FC<TransactionsTableProps> = ({ transactions, total, activePage, setActivePage }) => {
  const { t } = useTranslation();
  const { applicationDetails } = useApplications();

  const appRegion = applicationDetails?.region;

  const columns = useMemo(
    () => [
      {
        name: CompanyTransactionsColumnNames.AMOUNT,
        label: t('pages.lead.sections.paymentData.transactionsTable.columns.amount'),
        options: {
          customHeadRender,
          customBodyRender: (value: number) =>
            value ? parseMoney(value, mapAppRegionToSupportedCurrency(appRegion)) : null,
        },
      },
      {
        name: CompanyTransactionsColumnNames.CURRENCY,
        label: t('pages.lead.sections.paymentData.transactionsTable.columns.currency'),
        options: { customHeadRender },
      },
      {
        name: CompanyTransactionsColumnNames.TYPE,
        label: t('pages.lead.sections.paymentData.transactionsTable.columns.type'),
        options: { customHeadRender },
      },
      {
        name: CompanyTransactionsColumnNames.DATE,
        label: t('pages.lead.sections.paymentData.transactionsTable.columns.date'),
        options: {
          customHeadRender,
          customBodyRender: (value: string) => (value ? new Date(value).toLocaleDateString() : null),
        },
      },
    ],
    [appRegion, t],
  );

  const onTableChange = useCallback(
    (action: string, tableState: MUIDataTableState) => {
      if (action === 'changePage') {
        setActivePage(tableState.page);
      }
    },
    [setActivePage],
  );

  return (
    <Table<CompanyTransaction>
      columns={columns}
      data={transactions}
      count={total}
      page={activePage}
      rowsPerPage={ROWS_PER_TABLE_PAGE}
      onTableChange={onTableChange}
    />
  );
};

export default TransactionsTable;
